export type Area = "control" | "health" | "cctv";

export const AREA_ROOT: Record<Area, string> = {
  control: "/control",
  health: "/health",
  cctv: "/cctv",
};

export const controlPath = (siteId?: number | string | null) =>
  siteId == null ? AREA_ROOT.control : `${AREA_ROOT.control}/site/${siteId}`;

export const healthPath = (siteId?: number | string | null) =>
  siteId == null ? AREA_ROOT.health : `${AREA_ROOT.health}/site/${siteId}`;

/**
 * 보건 현장은 두 화면이다 — 장비 상태를 보는 대시보드(`healthPath`)와 조치를 쓰는
 * 기록 화면. 영역을 누르면 대시보드가 나오고 기록은 그 안에서 들어간다.
 */
export const healthRecordsPath = (siteId: number | string) => `${healthPath(siteId)}/records`;

export const cctvPath = (siteId: number | string, mode: "live" | "playback" = "live") =>
  mode === "playback"
    ? `${AREA_ROOT.cctv}/site/${siteId}/playback`
    : `${AREA_ROOT.cctv}/site/${siteId}`;

export const ME_PATH = "/me";
export const LOGIN_PATH = "/login";
export const HOME_PATH = AREA_ROOT.control;

export const SITE_PARAM = "siteId";

export const SITE_MATCH_PATTERNS = [
  `${AREA_ROOT.control}/site/:${SITE_PARAM}`,
  `${AREA_ROOT.health}/site/:${SITE_PARAM}`,
  `${AREA_ROOT.health}/site/:${SITE_PARAM}/records`,
  `${AREA_ROOT.cctv}/site/:${SITE_PARAM}`,
  `${AREA_ROOT.cctv}/site/:${SITE_PARAM}/playback`,
] as const;

export function areaOf(pathname: string): Area | null {
  if (pathname === AREA_ROOT.control || pathname.startsWith(`${AREA_ROOT.control}/`)) {
    return "control";
  }
  if (pathname === AREA_ROOT.health || pathname.startsWith(`${AREA_ROOT.health}/`)) {
    return "health";
  }
  if (pathname === AREA_ROOT.cctv || pathname.startsWith(`${AREA_ROOT.cctv}/`)) return "cctv";
  return null;
}

/**
 * 영역의 루트 화면인가 — 전국(`/health`)이거나 현장 대시보드(`/health/site/17`).
 * 그 아래 화면(`/records` · `/playback`)은 아니다.
 *
 * 뒤로가기 버튼이 여기서 갈린다. 메뉴가 GNB 로 올라간 뒤 영역 루트는 "돌아갈 위"가
 * 없다 — 관제에서 보건으로 가는 건 뒤가 아니라 옆이다.
 */
export function isAreaRoot(pathname: string): boolean {
  if (areaOf(pathname) == null) return false;
  return pathname.split("/").filter(Boolean).length <= 3;
}

export function isPlayback(pathname: string): boolean {
  return pathname.endsWith("/playback");
}

export function areaPath(area: Area, siteId?: number | string | null): string {
  if (area === "control") return controlPath(siteId);
  if (area === "health") return healthPath(siteId);
  return siteId == null ? AREA_ROOT.cctv : cctvPath(siteId);
}

export const LEGACY_REDIRECTS: { from: string; to: (siteId: string) => string }[] = [
  { from: "/site/:siteId", to: (id) => controlPath(id) },
  { from: "/safety-report/:siteId", to: (id) => healthRecordsPath(id) },
  { from: "/cctv-ai/:siteId", to: (id) => cctvPath(id) },
  { from: "/cctv-playback/:siteId", to: (id) => cctvPath(id, "playback") },
];
